import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  cancelIssueCoupon,
  createAdditionalCoupons,
  CreateAdditionalCouponsDto,
  disposeAndReissueCoupons,
  disposeCoupons,
  issueCoupon,
  promoteToReissued,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  cancelGiftOrder,
  partialCancelGiftOrder,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftOrderItemType,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { LowerRole } from '@shared/types';

import { isCouponCancellable, isCouponDisposable, resolveGiftItemId } from './constants';
import {
  CouponDetailModalProps,
  CouponDetailState,
  GiftItemEmbed,
  ReissueStatPerItem,
} from './types';

export function useCouponDetailState({
  coupons,
  orderId,
  order,
  onClose,
}: CouponDetailModalProps): CouponDetailState {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState<string | null>(null);
  const [expandedShareHistory, setExpandedShareHistory] = useState<Set<string>>(
    new Set(),
  );
  const [showAdditionalForm, setShowAdditionalForm] = useState(false);
  const [additionalCount, setAdditionalCount] = useState(1);
  const [isCreatingAdditional, setIsCreatingAdditional] = useState(false);
  const [additionalResult, setAdditionalResult] = useState<string | null>(null);
  const [reissueGiftItemId, setReissueGiftItemId] = useState('');

  const [disposeMode, setDisposeMode] = useState(false);
  const [disposeAction, setDisposeAction] = useState<'dispose' | 'dispose-reissue'>(
    'dispose-reissue',
  );
  const [disposeSelectedIds, setDisposeSelectedIds] = useState<Set<string>>(new Set());
  const [showDisposeConfirm, setShowDisposeConfirm] = useState(false);
  const [disposeReason, setDisposeReason] = useState('');
  const [isDisposing, setIsDisposing] = useState(false);
  const [disposeResult, setDisposeResult] = useState<string | null>(null);
  const [isPromoting, setIsPromoting] = useState(false);
  const [previewCopied, setPreviewCopied] = useState(false);

  const sortedCoupons = useMemo(
    () => [...coupons].sort((a, b) => a.seq - b.seq),
    [coupons],
  );

  const giftItemEmbedMap = useMemo(() => {
    const map = new Map<string, GiftItemEmbed>();
    if (!order?.orderDetails) return map;
    for (const d of order.orderDetails) {
      if (d.type !== GiftOrderItemType.Coupon) continue;
      const gid = resolveGiftItemId(d.giftItem);
      if (!gid) continue;
      if (!map.has(gid)) {
        map.set(gid, {
          name: d.giftItemEmbed.name,
          priceRetail: d.giftItemEmbed.priceRetail,
        });
      }
    }
    return map;
  }, [order]);

  const stats = useMemo(() => {
    const activeCoupons = coupons.filter(
      (c) => c.redeemStatus !== GiftCouponRedeemStatus.Disposed,
    );
    const originalCoupons = activeCoupons.filter((c) => !c.isAdditional);
    const additionalCoupons = activeCoupons.filter((c) => c.isAdditional);
    const disposedCount = coupons.filter(
      (c) => c.redeemStatus === GiftCouponRedeemStatus.Disposed,
    ).length;
    const reissuedCount = coupons.filter((c) => c.isReissued).length;
    const total = originalCoupons.length;
    const additionalTotal = additionalCoupons.length;
    const notShared = activeCoupons.filter(
      (c) => c.shareStatus === GiftCouponShareStatus.NotShared,
    ).length;
    const available = activeCoupons.filter(
      (c) =>
        c.redeemStatus !== GiftCouponRedeemStatus.Delivered &&
        c.redeemStatus !== GiftCouponRedeemStatus.Refunded &&
        c.redeemStatus !== GiftCouponRedeemStatus.Canceled,
    ).length;
    const delivered = activeCoupons.filter(
      (c) => c.redeemStatus === GiftCouponRedeemStatus.Delivered,
    ).length;
    return {
      total,
      additionalTotal,
      disposedCount,
      reissuedCount,
      notShared,
      available,
      delivered,
    };
  }, [coupons]);

  const reissueStatsByItem = useMemo<ReissueStatPerItem[]>(() => {
    const itemIds = Array.from(giftItemEmbedMap.keys());
    if (itemIds.length === 0) return [];

    return itemIds
      .map((gid) => {
        const embed = giftItemEmbedMap.get(gid);
        if (!embed) return null;
        const itemCoupons = coupons.filter((c) => c.giftItem === gid);
        const active = itemCoupons.filter(
          (c) => c.redeemStatus !== GiftCouponRedeemStatus.Disposed,
        );
        const originals = active.filter((c) => !c.isAdditional);
        const sharedNotViewed = originals.filter(
          (c) => c.shareStatus === GiftCouponShareStatus.Shared,
        ).length;
        const additionalIssued = active.filter((c) => c.isAdditional).length;
        return {
          giftItemId: gid,
          name: embed.name,
          priceRetail: embed.priceRetail,
          sharedNotViewed,
          additionalIssued,
          remaining: sharedNotViewed - additionalIssued,
        };
      })
      .filter((v): v is ReissueStatPerItem => v !== null);
  }, [coupons, giftItemEmbedMap]);

  const selectedReissueStat = useMemo(
    () => reissueStatsByItem.find((s) => s.giftItemId === reissueGiftItemId),
    [reissueStatsByItem, reissueGiftItemId],
  );

  const totalReissueRemaining = useMemo(
    () => reissueStatsByItem.reduce((sum, s) => sum + Math.max(0, s.remaining), 0),
    [reissueStatsByItem],
  );

  useEffect(() => {
    const first = reissueStatsByItem[0];
    if (first && !reissueStatsByItem.some((s) => s.giftItemId === reissueGiftItemId)) {
      setReissueGiftItemId(first.giftItemId);
    }
  }, [reissueStatsByItem, reissueGiftItemId]);

  useEffect(() => {
    if (!selectedReissueStat) return;
    const max = Math.max(1, selectedReissueStat.remaining);
    setAdditionalCount((prev) => Math.min(Math.max(1, prev), max));
  }, [selectedReissueStat]);

  const cancellableCoupons = useMemo(
    () => sortedCoupons.filter(isCouponCancellable),
    [sortedCoupons],
  );

  const canCancel = useMemo(() => {
    if (!orderId || !order) return false;
    const status = order.paymentDetail.status;
    return (
      status === GiftOrderPaymentStatus.Paid ||
      status === GiftOrderPaymentStatus.Confirmed
    );
  }, [orderId, order]);

  const hasDeliveryItems = useMemo(() => {
    if (!order) return false;
    return order.orderDetails.some((d) => d.type === GiftOrderItemType.Delivery);
  }, [order]);

  const allNotSharedDeliveries = useMemo(() => {
    if (!order || !order.deliveries) return true;
    const deliveries = order.deliveries as {
      shareStatus: string;
      deliveryStatus?: string;
    }[];
    return deliveries.every((d) => d.shareStatus === 'NotShared');
  }, [order]);

  const isFullCancel = useMemo(() => {
    if (!canCancel) return false;
    const activeCoupons = coupons.filter(
      (c) => c.redeemStatus !== GiftCouponRedeemStatus.Canceled,
    );
    const allCouponsSelected =
      activeCoupons.length > 0 && activeCoupons.every((c) => selectedIds.has(c._id));
    if (!allCouponsSelected) return false;
    if (hasDeliveryItems) return allNotSharedDeliveries;
    return true;
  }, [canCancel, coupons, selectedIds, hasDeliveryItems, allNotSharedDeliveries]);

  const disposableCoupons = useMemo(
    () => sortedCoupons.filter(isCouponDisposable),
    [sortedCoupons],
  );

  // --- Callbacks ---

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleShareHistory = useCallback((id: string) => {
    setExpandedShareHistory((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === cancellableCoupons.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(cancellableCoupons.map((c) => c._id)));
    }
  }, [cancellableCoupons, selectedIds.size]);

  const handleCancelRequest = useCallback(async () => {
    if (!orderId || selectedIds.size === 0 || !cancelReason.trim()) return;

    setIsCancelling(true);
    setCancelResult(null);

    try {
      if (isFullCancel) {
        await cancelGiftOrder(
          { _id: orderId, cancelReason: cancelReason.trim() },
          LowerRole.Admin,
        );
        setCancelResult('전체취소 요청이 완료되었습니다.');
      } else {
        const result = await partialCancelGiftOrder({
          _id: orderId,
          cancelReason: cancelReason.trim(),
          couponIds: Array.from(selectedIds),
          deliveryIds: [],
        });
        setCancelResult(
          `부분취소 완료: ${result.data.cancelledPrice.toLocaleString()}원 환불`,
        );
      }
      setShowCancelConfirm(false);
      setSelectedIds(new Set());
      setCancelReason('');
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : '취소 처리 중 오류가 발생했습니다.';
      setCancelResult(`오류: ${message}`);
    } finally {
      setIsCancelling(false);
    }
  }, [orderId, selectedIds, cancelReason, isFullCancel, onClose]);

  const handleCreateAdditional = useCallback(async () => {
    if (!orderId || additionalCount < 1 || !reissueGiftItemId) return;
    const stat = reissueStatsByItem.find((s) => s.giftItemId === reissueGiftItemId);
    if (!stat || additionalCount > stat.remaining) {
      setAdditionalResult(
        `오류: 재발행 가능 수량을 초과합니다. (최대 ${stat?.remaining ?? 0}개)`,
      );
      return;
    }

    setIsCreatingAdditional(true);
    setAdditionalResult(null);

    try {
      const dto: CreateAdditionalCouponsDto = {
        giftOrderId: orderId,
        count: additionalCount,
        giftItemId: reissueGiftItemId,
      };
      const created = await createAdditionalCoupons(dto);
      setAdditionalResult(`재발행 완료: ${created.length}개 쿠폰이 생성되었습니다.`);
      setShowAdditionalForm(false);
      setAdditionalCount(1);
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : '재발행 처리 중 오류가 발생했습니다.';
      setAdditionalResult(`오류: ${message}`);
    } finally {
      setIsCreatingAdditional(false);
    }
  }, [orderId, additionalCount, reissueGiftItemId, reissueStatsByItem, onClose]);

  const toggleDisposeSelect = useCallback((id: string) => {
    setDisposeSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleDisposeSelectAll = useCallback(() => {
    if (disposeSelectedIds.size === disposableCoupons.length) {
      setDisposeSelectedIds(new Set());
    } else {
      setDisposeSelectedIds(new Set(disposableCoupons.map((c) => c._id)));
    }
  }, [disposableCoupons, disposeSelectedIds.size]);

  const handleDispose = useCallback(async () => {
    if (disposeSelectedIds.size === 0) return;

    setIsDisposing(true);
    setDisposeResult(null);

    try {
      if (disposeAction === 'dispose') {
        const disposed = await disposeCoupons({
          couponIds: Array.from(disposeSelectedIds),
          reason: disposeReason.trim() || undefined,
        });
        setDisposeResult(`폐기 ${disposed.length}건 완료`);
      } else {
        const result = await disposeAndReissueCoupons({
          couponIds: Array.from(disposeSelectedIds),
          reason: disposeReason.trim() || undefined,
        });
        setDisposeResult(
          `폐기 ${result.disposed.length}건 + 재발급 ${result.reissued.length}건 완료`,
        );
      }
      setShowDisposeConfirm(false);
      setDisposeSelectedIds(new Set());
      setDisposeReason('');
      setDisposeMode(false);
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '처리 중 오류가 발생했습니다.';
      setDisposeResult(`오류: ${message}`);
    } finally {
      setIsDisposing(false);
    }
  }, [disposeSelectedIds, disposeReason, disposeAction, onClose]);

  const handlePromoteToReissued = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '이 재발행 쿠폰을 재발급(정식 대체)으로 승격하시겠습니까?\nisAdditional → isReissued로 변경되며, 통계에서 일반 쿠폰으로 집계됩니다.',
        )
      )
        return;
      setIsPromoting(true);
      setDisposeResult(null);
      try {
        await promoteToReissued(couponId);
        setDisposeResult('승격 완료: 재발행 → 재발급으로 변경되었습니다.');
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 1500);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : '승격 처리 중 오류가 발생했습니다.';
        setDisposeResult(`오류: ${message}`);
      } finally {
        setIsPromoting(false);
      }
    },
    [onClose],
  );

  // --- Refund/Issue result ---
  const [refundResult, setRefundResult] = useState<string | null>(null);

  // --- Issue (환불/연장 링크 발행) ---

  // --- Issue (환불/연장 링크 발행) ---
  const [isIssuing, setIsIssuing] = useState(false);

  const handleIssueRefund = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '환불 링크를 발행하시겠습니까?\n고객에게 환불 신청 페이지 링크가 생성됩니다.',
        )
      )
        return;
      setIsIssuing(true);
      setRefundResult(null);
      try {
        await issueCoupon({ couponId, type: 'refund' });
        const refundUrl = `${window.location.origin}/gift-store/coupon/${couponId}/refund`;
        await navigator.clipboard.writeText(refundUrl);
        setRefundResult(`환불 링크가 발행되고 클립보드에 복사되었습니다.`);
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 2000);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Issue 발행 중 오류가 발생했습니다.';
        setRefundResult(`오류: ${message}`);
      } finally {
        setIsIssuing(false);
      }
    },
    [onClose],
  );

  const handleIssueRenewal = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '연장 링크를 발행하시겠습니까?\n고객에게 연장 신청 페이지 링크가 생성됩니다.',
        )
      )
        return;
      setIsIssuing(true);
      setRefundResult(null);
      try {
        await issueCoupon({ couponId, type: 'renewal' });
        const renewalUrl = `${window.location.origin}/gift-store/coupon/${couponId}/renewal`;
        await navigator.clipboard.writeText(renewalUrl);
        setRefundResult(`연장 링크가 발행되고 클립보드에 복사되었습니다.`);
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 2000);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : '연장 링크 발행 중 오류가 발생했습니다.';
        setRefundResult(`오류: ${message}`);
      } finally {
        setIsIssuing(false);
      }
    },
    [onClose],
  );

  const handleCancelIssue = useCallback(
    async (couponId: string) => {
      if (!confirm('발행된 링크를 취소하시겠습니까?')) return;
      setIsIssuing(true);
      setRefundResult(null);
      try {
        await cancelIssueCoupon(couponId);
        setRefundResult('링크 발행이 취소되었습니다.');
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 1500);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Issue 취소 중 오류가 발생했습니다.';
        setRefundResult(`오류: ${message}`);
      } finally {
        setIsIssuing(false);
      }
    },
    [onClose],
  );

  return {
    sortedCoupons,
    giftItemEmbedMap,
    stats,
    reissueStatsByItem,
    selectedReissueStat,
    totalReissueRemaining,
    cancellableCoupons,
    disposableCoupons,
    canCancel,
    hasDeliveryItems,
    isFullCancel,

    selectedIds,
    cancelReason,
    setCancelReason,
    showCancelConfirm,
    setShowCancelConfirm,
    isCancelling,
    cancelResult,
    toggleSelect,
    toggleSelectAll,
    handleCancelRequest,

    showAdditionalForm,
    setShowAdditionalForm,
    additionalCount,
    setAdditionalCount,
    isCreatingAdditional,
    additionalResult,
    reissueGiftItemId,
    setReissueGiftItemId,
    handleCreateAdditional,

    disposeMode,
    setDisposeMode,
    disposeAction,
    setDisposeAction,
    disposeSelectedIds,
    setDisposeSelectedIds,
    showDisposeConfirm,
    setShowDisposeConfirm,
    disposeReason,
    setDisposeReason,
    isDisposing,
    disposeResult,
    toggleDisposeSelect,
    toggleDisposeSelectAll,
    handleDispose,

    isPromoting,
    handlePromoteToReissued,

    // refund/issue result
    refundResult,

    // issue
    isIssuing,
    handleIssueRefund,
    handleIssueRenewal,
    handleCancelIssue,

    expandedShareHistory,
    toggleShareHistory,

    previewCopied,
    setPreviewCopied,
  };
}
